
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Settings, Package, ShoppingCart, Wrench } from "lucide-react";
import type { RentalProduct } from '@/types/entities';
import type { OrderStoreItem } from '@/hooks/useOrderStoreItems';

type RentalKit = {
  id: string;
  name: string;
  price?: number | null;
  isAvailable?: boolean;
  available?: boolean;
};

interface OrderSummarySectionProps {
  totalAmount: number;
  selectedInventoryItems: Array<{ id: string; quantity: number }>;
  selectedStoreItems: Array<{ id: string; quantity: number }>;
  selectedKits: string[];
  inventoryItems: RentalProduct[];
  storeItems: OrderStoreItem[];
  rentalKits: RentalKit[];
}

export const OrderSummarySection: React.FC<OrderSummarySectionProps> = ({
  totalAmount,
  selectedInventoryItems,
  selectedStoreItems,
  selectedKits,
  inventoryItems,
  storeItems,
  rentalKits
}) => {
  const getSelectedInventoryDetails = () => {
    return selectedInventoryItems.map(selected => {
      const item = inventoryItems.find(inv => inv.id === selected.id);
      return item ? { ...item, quantity: selected.quantity } : null;
    }).filter((item): item is RentalProduct & { quantity: number } => Boolean(item));
  };

  const getSelectedStoreDetails = () => {
    return selectedStoreItems.map(selected => {
      const item = storeItems.find(store => store.id === selected.id);
      return item ? { ...item, quantity: selected.quantity } : null;
    }).filter(Boolean);
  };

  const getSelectedKitDetails = () => {
    return selectedKits.map(kitId => {
      const kit = rentalKits.find(k => k.id === kitId);
      return kit || null;
    }).filter(Boolean);
  };

  const inventoryDetails = getSelectedInventoryDetails();
  const storeDetails = getSelectedStoreDetails();
  const kitDetails = getSelectedKitDetails();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Settings className="mr-2 h-5 w-5" />
          Итоговая информация
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Инвентарь для аренды */}
        {inventoryDetails.length > 0 && (
          <div>
            <h4 className="font-medium flex items-center mb-2">
              <Wrench className="mr-2 h-4 w-4 text-blue-600" />
              Инвентарь для аренды ({inventoryDetails.length})
            </h4>
            <div className="space-y-1">
              {inventoryDetails.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>{item.name} x{item.quantity}</span>
                  <span>{((item.dailyPrice || 0) * item.quantity).toLocaleString()} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Комплекты */}
        {kitDetails.length > 0 && (
          <div>
            <h4 className="font-medium flex items-center mb-2">
              <Package className="mr-2 h-4 w-4 text-green-600" />
              Комплекты ({kitDetails.length})
            </h4>
            <div className="space-y-1">
              {kitDetails.map(kit => (
                <div key={kit.id} className="flex justify-between text-sm">
                  <span>{kit.name}</span>
                  <span>{(kit.price || 0).toLocaleString()} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Товары для покупки */}
        {storeDetails.length > 0 && (
          <div>
            <h4 className="font-medium flex items-center mb-2">
              <ShoppingCart className="mr-2 h-4 w-4 text-orange-600" />
              Товары для покупки ({storeDetails.length})
            </h4>
            <div className="space-y-1">
              {storeDetails.map(item => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span>{item.name} x{item.quantity}</span>
                  <span>{(item.price * item.quantity).toLocaleString()} ₽</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Итоговая сумма */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Общая сумма:</span>
            <Badge variant="default" className="text-lg px-3 py-1">
              {totalAmount.toLocaleString()} ₽
            </Badge>
          </div>
        </div>

        {/* Если ничего не выбрано */}
        {inventoryDetails.length === 0 && storeDetails.length === 0 && kitDetails.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <p>Ничего не выбрано для заказа</p>
            <p className="text-xs mt-1">Выберите товары или услуги выше</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
